import React from 'react';
import { GeoJsonLayer, TextLayer } from '@deck.gl/layers';
import { DeckGL } from '@deck.gl/react';
import { WebMercatorViewport, type Layer } from '@deck.gl/core';
import { useUSStatesGeo } from './useUSStatesGeo';
import { transformCoord, transformFeature, NAME_TO_ABBR, STATE_LABEL_RAW } from './usMapProjection';

interface InteractiveStatesMapProps {
  selectedStates: string[];
  onChange: (next: string[]) => void;
  accentColor: string;
  // 2-letter codes the user may toggle. Disallowed states render dimmed
  // and clicks/hover are no-ops on them. When null/undefined, all states
  // are allowed (legacy stations with no declared coveredStates).
  allowedStates?: string[] | null;
  // 2-letter codes whose geometry the initial view should fit. When set,
  // computes a bounding box from those features and derives a centered
  // zoom; when unset the map opens on the national view (zoom 2.8).
  focusStates?: string[];
}

// Walk a feature's coordinate tree and accumulate min/max lng+lat. Handles
// Polygon + MultiPolygon (everything we'd see in the US-states GeoJSON).
function expandBbox(
  geom: GeoJSON.Geometry,
  bbox: [number, number, number, number],
): void {
  const visit = (c: unknown): void => {
    if (typeof (c as number[])[0] === 'number') {
      const [lng, lat] = c as [number, number];
      if (lng < bbox[0]) bbox[0] = lng;
      if (lat < bbox[1]) bbox[1] = lat;
      if (lng > bbox[2]) bbox[2] = lng;
      if (lat > bbox[3]) bbox[3] = lat;
      return;
    }
    for (const child of c as unknown[]) visit(child);
  };
  if (geom.type === 'Polygon' || geom.type === 'MultiPolygon') {
    visit(geom.coordinates);
  }
}

const InteractiveStatesMap: React.FC<InteractiveStatesMapProps> = ({
  selectedStates,
  onChange,
  accentColor,
  allowedStates,
  focusStates,
}) => {
  const { data: rawGeoJson } = useUSStatesGeo();
  const [hoveredState, setHoveredState] = React.useState<string | null>(null);

  // Apply the AK/HI/PR repositioning to the raw GeoJSON only once per
  // payload. Cached via useMemo so re-renders don't re-walk every polygon.
  const statesGeoJson = React.useMemo<GeoJSON.FeatureCollection | null>(() => {
    if (!rawGeoJson) return null;
    return { ...rawGeoJson, features: rawGeoJson.features.map(transformFeature) };
  }, [rawGeoJson]);

  const selectedSet = React.useMemo(() => new Set(selectedStates), [selectedStates]);

  // null/undefined here ≡ "no restriction" (full national map, every state
  // selectable). When the caller passes a list we honor it strictly.
  const allowedSet = React.useMemo<Set<string> | null>(() => {
    if (!allowedStates) return null;
    return new Set(allowedStates);
  }, [allowedStates]);

  const toggleState = React.useCallback(
    (abbr: string) => {
      if (allowedSet && !allowedSet.has(abbr)) return;
      if (selectedSet.has(abbr)) {
        onChange(selectedStates.filter((s) => s !== abbr));
      } else {
        onChange([...selectedStates, abbr]);
      }
    },
    [allowedSet, selectedSet, selectedStates, onChange],
  );

  // For each state, two things: the viewport bbox the map will show, and
  // a fallback "visible bbox center" we can use when a label's hand-tuned
  // default falls outside that viewport. Bounding-box center (not vertex
  // centroid) avoids the coastline-vertex bias.
  // Empty when focusStates is unset; national-view labels stay at the
  // static hand-tuned positions in STATE_LABEL_RAW.
  const labelRelocation = React.useMemo<{
    viewportBbox: [number, number, number, number] | null;
    centroidsByAbbr: Record<string, [number, number]>;
  }>(() => {
    if (!statesGeoJson || !focusStates || focusStates.length === 0) {
      return { viewportBbox: null, centroidsByAbbr: {} };
    }
    // Re-derive the viewport bbox: fit the focus polygons → ask the
    // resulting viewport for its actual visible bounds. This includes the
    // padding we baked into fitBounds, which extends well past focusBbox.
    const focusBbox: [number, number, number, number] = [180, 90, -180, -90];
    const focus = new Set(focusStates);
    for (const feat of statesGeoJson.features) {
      const name = (feat.properties as Record<string, string> | null)?.name ?? '';
      const abbr = NAME_TO_ABBR[name];
      if (!abbr || !focus.has(abbr)) continue;
      if (feat.geometry) expandBbox(feat.geometry, focusBbox);
    }
    if (focusBbox[0] > focusBbox[2] || focusBbox[1] > focusBbox[3]) {
      return { viewportBbox: null, centroidsByAbbr: {} };
    }
    let viewportBbox: [number, number, number, number];
    try {
      const fit = new WebMercatorViewport({ width: 800, height: 420 }).fitBounds(
        [
          [focusBbox[0], focusBbox[1]],
          [focusBbox[2], focusBbox[3]],
        ],
        { padding: 140 },
      );
      const visible = new WebMercatorViewport({
        width: 800,
        height: 420,
        longitude: fit.longitude,
        latitude: fit.latitude,
        zoom: fit.zoom,
      }).getBounds();
      // getBounds returns a flat [west, south, east, north] 4-tuple.
      viewportBbox = visible;
    } catch {
      return { viewportBbox: null, centroidsByAbbr: {} };
    }

    const centroids: Record<string, [number, number]> = {};
    const expandStateBbox = (
      coords: unknown,
      acc: [number, number, number, number],
    ): void => {
      if (typeof (coords as number[])[0] === 'number') {
        const [lng, lat] = coords as [number, number];
        if (
          lng >= viewportBbox[0] && lng <= viewportBbox[2] &&
          lat >= viewportBbox[1] && lat <= viewportBbox[3]
        ) {
          if (lng < acc[0]) acc[0] = lng;
          if (lat < acc[1]) acc[1] = lat;
          if (lng > acc[2]) acc[2] = lng;
          if (lat > acc[3]) acc[3] = lat;
        }
        return;
      }
      for (const c of coords as unknown[]) expandStateBbox(c, acc);
    };
    for (const feat of statesGeoJson.features) {
      const name = (feat.properties as Record<string, string> | null)?.name ?? '';
      const abbr = NAME_TO_ABBR[name];
      if (!abbr || !feat.geometry) continue;
      const geom = feat.geometry;
      if (geom.type !== 'Polygon' && geom.type !== 'MultiPolygon') continue;
      const stateBbox: [number, number, number, number] = [180, 90, -180, -90];
      expandStateBbox(geom.coordinates, stateBbox);
      if (stateBbox[0] <= stateBbox[2] && stateBbox[1] <= stateBbox[3]) {
        centroids[abbr] = [
          (stateBbox[0] + stateBbox[2]) / 2,
          (stateBbox[1] + stateBbox[3]) / 2,
        ];
      }
    }
    return { viewportBbox, centroidsByAbbr: centroids };
  }, [statesGeoJson, focusStates]);

  const labels = React.useMemo(
    () =>
      STATE_LABEL_RAW.flatMap(([text, lng, lat]) => {
        // Zoomed view: only show labels for allowed states (we don't want
        // to render text on dimmed, non-selectable states). Use the
        // hand-tuned default position if it's inside the viewport;
        // otherwise fall back to the visible-bbox center for that state
        // so a partially-visible state still gets a label in-frame.
        if (focusStates && focusStates.length > 0) {
          if (allowedSet && !allowedSet.has(text)) return [];
          const { viewportBbox, centroidsByAbbr } = labelRelocation;
          if (!viewportBbox) return [];
          const [defaultLng, defaultLat] = transformCoord(lng, lat);
          const defaultInside =
            defaultLng >= viewportBbox[0] && defaultLng <= viewportBbox[2] &&
            defaultLat >= viewportBbox[1] && defaultLat <= viewportBbox[3];
          if (defaultInside) {
            return [{ position: [defaultLng, defaultLat] as [number, number], text }];
          }
          const fallback = centroidsByAbbr[text];
          if (!fallback) return [];
          return [{ position: fallback, text }];
        }
        const [tLng, tLat] = transformCoord(lng, lat);
        return [{ position: [tLng, tLat] as [number, number], text }];
      }),
    [labelRelocation, focusStates, allowedSet],
  );

  // Parse the accent color (hex, "#RGB" or "#RRGGBB") into the RGBA tuple
  // deck.gl wants. Defaults silently to a neutral blue if the prop isn't a
  // recognizable hex — the picker still works without color customization.
  const accentRgb = React.useMemo<[number, number, number]>(() => {
    const hex = accentColor.replace('#', '');
    const expand = hex.length === 3 ? hex.split('').map((c) => c + c).join('') : hex;
    if (!/^[0-9a-f]{6}$/i.test(expand)) return [37, 99, 235];
    return [
      parseInt(expand.slice(0, 2), 16),
      parseInt(expand.slice(2, 4), 16),
      parseInt(expand.slice(4, 6), 16),
    ];
  }, [accentColor]);

  const layers = React.useMemo(() => {
    const result: Layer[] = [];

    if (statesGeoJson) {
      result.push(
        new GeoJsonLayer({
          id: 'states-interactive',
          data: statesGeoJson,
          filled: true,
          stroked: true,
          pickable: true,
          getFillColor: (f: GeoJSON.Feature) => {
            const name = (f.properties as Record<string, string>)?.name || '';
            const abbr = NAME_TO_ABBR[name] || '';
            if (selectedSet.has(abbr)) return [...accentRgb, 255];
            // Disallowed states render as a flat very-light gray with no
            // hover affordance — visually present but clearly inert.
            if (allowedSet && !allowedSet.has(abbr)) return [241, 245, 249, 255];
            if (hoveredState === abbr) return [...accentRgb, 110];
            return [224, 231, 239, 255];
          },
          getLineColor: [255, 255, 255, 255],
          getLineWidth: 1,
          lineWidthUnits: 'pixels' as const,
          onClick: (info: { object?: GeoJSON.Feature }) => {
            if (!info.object) return;
            const name = (info.object.properties as Record<string, string>)?.name || '';
            const abbr = NAME_TO_ABBR[name];
            if (abbr) toggleState(abbr);
          },
          onHover: (info: { object?: GeoJSON.Feature }) => {
            if (info.object) {
              const name = (info.object.properties as Record<string, string>)?.name || '';
              const abbr = NAME_TO_ABBR[name] || null;
              // Skip hover highlight on disallowed states so the user
              // doesn't get false-positive interactivity cues.
              if (allowedSet && abbr && !allowedSet.has(abbr)) {
                setHoveredState(null);
              } else {
                setHoveredState(abbr);
              }
            } else {
              setHoveredState(null);
            }
          },
          updateTriggers: {
            getFillColor: [selectedStates, hoveredState, accentRgb, allowedStates],
          },
        }),
      );
    }

    if (labels.length > 0) {
      result.push(
        new TextLayer({
          id: 'state-labels',
          data: labels,
          getPosition: (d: { position: number[] }) => d.position as [number, number],
          getText: (d: { text: string }) => d.text,
          getSize: 11,
          getColor: (d: { text: string }) =>
            selectedSet.has(d.text) ? [255, 255, 255, 255] : [100, 116, 139, 255],
          fontWeight: 600,
          getTextAnchor: 'middle',
          getAlignmentBaseline: 'center',
          fontFamily: 'system-ui, -apple-system, sans-serif',
          sizeUnits: 'pixels' as const,
          billboard: false,
          updateTriggers: {
            getColor: [selectedStates],
          },
        }),
      );
    }

    return result;
  }, [statesGeoJson, labels, selectedSet, hoveredState, selectedStates, toggleState, accentRgb, allowedSet, allowedStates]);

  // Compute the initial map view. When focusStates is empty (no publisher
  // coverage declared), default to the national framing we've been using.
  // When set, walk the matching features' polygons → bounding box → fit
  // via deck.gl's WebMercatorViewport.fitBounds. Recomputed only when the
  // GeoJSON or the focus list changes — not on every selection.
  const initialViewState = React.useMemo(() => {
    const nationalDefault = { longitude: -97, latitude: 37, zoom: 2.8 };
    if (!statesGeoJson || !focusStates || focusStates.length === 0) {
      return nationalDefault;
    }
    const focus = new Set(focusStates);
    const bbox: [number, number, number, number] = [180, 90, -180, -90];
    for (const feat of statesGeoJson.features) {
      const name = (feat.properties as Record<string, string> | null)?.name ?? '';
      const abbr = NAME_TO_ABBR[name];
      if (!abbr || !focus.has(abbr)) continue;
      if (feat.geometry) expandBbox(feat.geometry, bbox);
    }
    // No matched features — fall back to national.
    if (bbox[0] > bbox[2] || bbox[1] > bbox[3]) return nationalDefault;
    try {
      // Map container is 100% of the 420px-tall wrapper; deck.gl's fitBounds
      // needs explicit width/height. Width is approximate (full available
      // panel width); deck.gl re-sizes on mount, so a small mismatch just
      // means the initial zoom is a touch under/over.
      const viewport = new WebMercatorViewport({ width: 800, height: 420 });
      const { longitude, latitude, zoom } = viewport.fitBounds(
        [
          [bbox[0], bbox[1]],
          [bbox[2], bbox[3]],
        ],
        // Higher padding = more breathing room around the covered states +
        // visible neighboring states. Tuned to leave a clear band of
        // adjacent (selectable) states visible at the edges.
        { padding: 140 },
      );
      return { longitude, latitude, zoom };
    } catch {
      return nationalDefault;
    }
  }, [statesGeoJson, focusStates]);

  return (
    <div
      className="rounded-lg border border-slate-200 overflow-hidden relative bg-white"
      style={{ height: 420 }}
      // deck.gl's onHover doesn't always fire with no info when the cursor
      // exits the canvas across an edge (no intermediate pointer event in
      // empty whitespace), so the hover highlight stays "stuck" on the
      // last state. Clearing on the outer div's mouseleave covers every
      // exit direction.
      onMouseLeave={() => setHoveredState(null)}
    >
      <DeckGL
        initialViewState={initialViewState}
        controller={{
          scrollZoom: false,
          dragPan: false,
          dragRotate: false,
          doubleClickZoom: false,
          touchZoom: false,
          touchRotate: false,
          keyboard: false,
        }}
        layers={layers}
        style={{ position: 'absolute', top: '0', left: '0', width: '100%', height: '100%', background: '#ffffff' }}
        getCursor={({ isHovering }: { isHovering: boolean }) => (isHovering ? 'pointer' : 'default')}
      />
    </div>
  );
};

export default InteractiveStatesMap;
